import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@plato/ui/containers/alert-dialog';
import { cn } from '@plato/ui/util/classes';
import type { LucideProps } from 'lucide-react';
import type { PropsWithChildren, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useToggle } from 'react-use';
import { Button } from '../ui/button.tsx';
import { DialogStateManager, useDialog } from './dialog-provider.tsx';
import type { DialogInnerProps } from './types.ts';

type Focus = 'submit' | 'destroy';

const ConfirmationDialogFocusContext = createContext<Focus>('submit');
function useConfirmationDialogFocus(): Focus {
    return useContext(ConfirmationDialogFocusContext);
}

type BaseConfirmationRejectButtonProps = {
    onClick: () => void;
} & PropsWithChildren;

function BaseConfirmationRejectButton({ children, onClick }: BaseConfirmationRejectButtonProps): ReactNode {
    return (
        <Button variant="subtle" onClick={onClick}>
            {children}
        </Button>
    );
}

type BaseConfirmationAcceptButtonProps = {
    isLoading?: boolean;
    onClick: () => void;
} & PropsWithChildren;

function BaseConfirmationAcceptButton({
    children,
    isLoading,
    onClick,
}: BaseConfirmationAcceptButtonProps): ReactNode {
    const focus = useConfirmationDialogFocus();

    return (
        <Button
            variant={
                {
                    submit: 'primary',
                    destroy: 'danger',
                }[focus] as 'primary' | 'danger'
            }
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onClick}
        >
            {children}
        </Button>
    );
}

type BaseConfirmationActionsProps = {
    className?: string;
} & PropsWithChildren;

// The body below already carries the surface's inset, so the footer contributes none
// of its own — it stays `AlertDialogFooter` to keep the slot and its future styling.
function BaseConfirmationActions({ children, className }: BaseConfirmationActionsProps): ReactNode {
    return <AlertDialogFooter className={cn('w-full p-0', className)}>{children}</AlertDialogFooter>;
}

type BaseConfirmationDialogProps = {
    icon: React.ComponentType<LucideProps>;
    title: string;
    description: React.ReactNode;
    focus?: Focus;
    className?: string;
} & PropsWithChildren;

export function BaseConfirmationDialog({
    icon: Icon,
    focus = 'submit',
    title,
    description,
    children,
    className,
}: BaseConfirmationDialogProps) {
    const { visible, remove } = useDialog();

    return (
        <ConfirmationDialogFocusContext.Provider value={focus}>
            <AlertDialog
                open={visible}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        remove();
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <div className="flex items-center gap-container-cozy">
                            <Icon
                                className={cn(
                                    'size-4 shrink-0',
                                    {
                                        submit: 'text-blue-500',
                                        destroy: 'text-danger',
                                    }[focus],
                                )}
                            />
                            <AlertDialogTitle>{title}</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription>{description}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <div
                        className={cn(
                            'flex w-full flex-col gap-container-cozy px-5 pt-container-cozy pb-5 sm:px-6',
                            className,
                        )}
                    >
                        {children}
                    </div>
                </AlertDialogContent>
            </AlertDialog>
        </ConfirmationDialogFocusContext.Provider>
    );
}

BaseConfirmationDialog.RejectButton = BaseConfirmationRejectButton;
BaseConfirmationDialog.AcceptButton = BaseConfirmationAcceptButton;
BaseConfirmationDialog.Actions = BaseConfirmationActions;

export type ConfirmationDialogProps = {
    accept: {
        text: string;
        action: () => Promise<void> | void;
    };
    reject: {
        text: string;
        action?: () => void;
    };
} & DialogInnerProps &
    BaseConfirmationDialogProps;

export const ConfirmationDialog = DialogStateManager.create(
    ({ icon, focus = 'submit', title, description, accept, reject, className }: ConfirmationDialogProps) => {
        const modal = useDialog();

        const [isLoading, toggleLoading] = useToggle(false);

        const handleAccept = async (): Promise<void> => {
            toggleLoading();

            try {
                await accept.action();
            } catch (e) {
                console.warn(e);
            }

            toggleLoading();
            void modal.hide();
        };

        const handleReject = (): void => {
            reject.action?.();
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                icon={icon}
                title={title}
                description={description}
                focus={focus}
                className={className}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleReject}>
                        {reject.text}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton isLoading={isLoading} onClick={handleAccept}>
                        {accept.text}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
